/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import clsx from "clsx";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import {useTranslation} from "react-i18next";
import BookingLoginReservationDetailInput
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationDetailInput";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useDispatch} from "useDispatch";
import * as yup from "yup";
import BookingLoginReservationButtonInner
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButtonInner";
import {BookingLoginReservationButtonSize} from "types/enums/BookingLoginReservationButtonSize";
import {BookingLoginReservationButtonStyle} from "types/enums/BookingLoginReservationButtonStyle";
import {CheckIcon, TrashIcon} from "@heroicons/react/24/solid";
import BookingLoginReservationButton
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButton";
import BookingAction from "store/booking/BookingAction";
import {Reservation} from "types/entities/Reservation";
import {toast} from "react-toastify";

interface LeadOriginFormInputs {
    lead_origin: string;
}

interface BookingSellerReservationLeadOriginProps {
    reservation: Reservation;
    className?: string;
}

const BookingSellerReservationLeadOrigin = (props: BookingSellerReservationLeadOriginProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const LeadOriginFormSchema = yup.object().shape({
        lead_origin: yup.string().required("Driver GUID is required."),
    });
    const formDefaultValues = {
        lead_origin: props.reservation.lead_origin ?? "",
    };
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: {errors},
    } = useForm<LeadOriginFormInputs>({
        resolver: yupResolver(LeadOriginFormSchema),
        defaultValues: {...formDefaultValues},
    });

    const onErrorHandler = (dataErrors: any) => {
        console.log("onErrorHandler", dataErrors);
    };

    const onSubmitHandler = (data: LeadOriginFormInputs) => {
        if (isLoading) {
            return true;
        }
        setIsLoading(true);

        dispatch(BookingAction.fetchSetReservationLeadOrigin(props.reservation.id, data.lead_origin))
            .then(r => {})
            .catch(err => {
                console.error(err);
            })
            .finally(() => {
                toast.success("Lead origin has been saved");
                setIsLoading(false);
            });
        return;
    };

    const onRemoveHandler = () => {
        dispatch(BookingAction.fetchRemoveReservationLeadOrigin(props.reservation.id))
            .then(r => {})
            .catch(err => {
                console.error(err);
            })
            .finally(() => {
                toast.success("Lead origin has been removed");
                reset();
                setIsLoading(false);
            });
    };

    useEffect(() => {
        setValue("lead_origin", props.reservation.lead_origin);
    }, [props.reservation.lead_origin]);

    return (
        <BookingCard className={clsx("BookingSellerReservationInfoCardLeadOrigin !px-0 !py-0 mb-6", props.className)}>
            <BookingCardTitle className={"!font-bold text-xl !text-black px-3 py-2.5 xl:px-3 xl:py-3 border-b"} title={t("Lead origin")} />
            <div className={"flex flex-col"}>
                <form autoComplete={"off"} className={"grid grid-cols-1 gap-y-3 px-3 py-3"} onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}>
                    <BookingLoginReservationDetailInput
                        className={"col-span-1"}
                        errors={errors}
                        label={t("Save the lead origin of the reservation")}
                        name={"lead_origin"}
                        register={register}
                        required={true}
                    />
                    <div className={"col-span-2 flex flex-row justify-center items-center gap-x-2"}>
                        <BookingLoginReservationButton className={"flex w-full"} onClick={handleSubmit(onSubmitHandler, onErrorHandler)}>
                            <BookingLoginReservationButtonInner
                                className={"inline-flex w-full"}
                                size={BookingLoginReservationButtonSize.Normal}
                                style={BookingLoginReservationButtonStyle.Green}
                            >
                                <CheckIcon className={"w-4 h-4 flex-none"} />
                                <span className={"flex ml-2"}>{t("Save")}</span>
                            </BookingLoginReservationButtonInner>
                        </BookingLoginReservationButton>

                        <BookingLoginReservationButton type={"button"} onClick={() => onRemoveHandler()}>
                            <BookingLoginReservationButtonInner
                                className={"py-3"}
                                size={BookingLoginReservationButtonSize.Normal}
                                style={BookingLoginReservationButtonStyle.Red}
                            >
                                <TrashIcon className={"w-4 h-4 flex-none"} />
                            </BookingLoginReservationButtonInner>
                        </BookingLoginReservationButton>
                    </div>
                </form>
            </div>
        </BookingCard>
    );
};

export default BookingSellerReservationLeadOrigin;
